import { useEffect, useState } from 'preact/hooks';
import { API, onEntry } from '../api.js';
import { zec, short } from '../format.js';
import type { SignedRequest } from '../../../src/core/types.js';

interface TapeItem {
  key: string;
  token: string;
  symbol: string;
  side: 'buy' | 'sell' | 'new' | 'launch';
  zat: string;
  user: string;
}

/** Live tape of the latest trades and launches. */
export function Tape() {
  const [items, setItems] = useState<TapeItem[]>([]);
  useEffect(() => {
    fetch(API + '/api/trades')
      .then((r) => r.json())
      .then((j) =>
        setItems(
          (j.trades ?? []).slice(0, 30).map((t: any) => ({ key: String(t.seq), token: t.token, symbol: t.symbol, side: t.side, zat: t.zat, user: t.user })),
        ),
      )
      .catch(() => {});
    return onEntry((e) => {
      if (e.kind !== 'user') return;
      const { req } = e.data as SignedRequest;
      const r = e.result as any;
      let it: TapeItem | null = null;
      // A buy in a launch minute is an order (filled later at one price), not a trade yet.
      if (req.body.type === 'buy')
        it = r.batched
          ? { key: String(e.seq), token: req.body.token, symbol: '', side: 'launch', zat: r.zat, user: req.user }
          : { key: String(e.seq), token: req.body.token, symbol: '', side: 'buy', zat: r.zatUsed, user: req.user };
      if (req.body.type === 'sell') it = { key: String(e.seq), token: req.body.token, symbol: '', side: 'sell', zat: r.zatOut, user: req.user };
      if (req.body.type === 'create') it = { key: String(e.seq), token: r.token, symbol: req.body.symbol, side: 'new', zat: '0', user: req.user };
      if (!it) return;
      const item = it;
      setItems((prev) => {
        const sym = item.symbol || prev.find((p) => p.token === item.token)?.symbol || item.token;
        return [{ ...item, symbol: sym }, ...prev].slice(0, 30);
      });
    });
  }, []);

  if (items.length === 0) return <div class="tape" aria-hidden="true" />;
  return (
    <div class="tape" aria-label="Latest trades">
      <div class="tape-track">
        {items.map((t) => (
          <a class="tape-item" href={`#/token/${t.token}`} key={t.key}>
            <b>${t.symbol}</b>
            {t.side === 'new' ? (
              <span>launched by {short(t.user)}</span>
            ) : t.side === 'launch' ? (
              <span class="up">joined the launch with {zec(t.zat)} ZEC</span>
            ) : (
              <span class={t.side === 'buy' ? 'up' : 'down'}>
                {t.side === 'buy' ? 'bought' : 'sold'} {zec(t.zat)} ZEC
              </span>
            )}
          </a>
        ))}
      </div>
    </div>
  );
}
